
import {ProjectElement}                    from '@/models/project/project.element';

import {Project}                           from '@/models/project';
import {Logger}                            from "@/util/logger";
import {basename, dirname}                 from "@/util/util";

import {CorridorBatchDialogParams}         from "@/gui/dialogs/corridor.batch.dialog.params";
import {CorridorGroupBatch}                from "@/models/batch/corridor.group.batch";
import {BatchCell}                         from "@/models/batch/batch.cell";
import {Observable}                        from "rxjs";
import {CorridorBatch}                     from "@/models/batch/corridor.batch";
import {SimulationElement}                 from "@/models/project/simulation.element";
import {ProjectElementFactory}             from "@/models/project.element.factory";
import {ProjectElementType}                from "@/models/project/project.element.type";
import {BatchGroupElement}                 from "@/models/project/batch.group.element";
import { v4 as uuidv4 }                    from 'uuid';
import {CorridorBatchElement}              from "@/models/project/corridor.batch.element";
import {BlobType}                          from "@/models/blob";
import {TaskUpdate} from "@/models/task.update";
import {TaskCreateBatch} from "@/models/task.create.batch";
import {TaskCreateBatchFailed} from "@/models/task.create.batch.failed";
import {TaskCreateBatchComplete} from "@/models/task.create.batch.complete";

/**
 *
 * @class CorridorBatchCreator
 *
 * helper to take the parameters from a new corridor batch dialog (name and import directory)
 * and launch the creation of a batch node (and its children) in the project tree.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    William Au
 * @date      February 2022
 *
 */
export class CorridorBatchCreator {

  /** the user's current project */
  private _project: Project;

  /** the long-running task progress item */
  private _taskId: string = '';

  /** the number of corridors (batches) in the batch of batches */
  private _numCorridors: number = 0;

  /** standard constructor */
  constructor(
    project: Project
  ) {

    this._project = project;
    let cbc: CorridorBatchCreator = this;

    Logger.debug('[CorridorBatchCreator] adding Corridor Batch...');

    /* go through a dialog to get the name and parameters for the model */

    cbc._project.services.modals.open(
      'new-corridorbatch-modal',
      (value: CorridorBatchDialogParams, resolve, reject) => {

        let err: string = cbc.validate(value);

        if (err != '') {

          reject(err);
        }

        resolve(value);
      },
      (value: CorridorBatchDialogParams) => {

        cbc.create(value);

      });

  }

  /** the dialog parameters wer accepted, time to create a batch! */
  create(value: CorridorBatchDialogParams) {

    let cbc: CorridorBatchCreator = this;

    cbc._taskId = TaskUpdate.generateId();

    /* checkpoint: scanning corridors */

    cbc._project.services.taskMonitor.progress(new TaskCreateBatch(cbc._taskId,  `Scanning for batches of ${value.batch}...`, 0.0));

    Logger.debug(`[CorridorBatchCreator] create - scanning: `, value);

    cbc._project.services.swe.corridorImportScan(value.src, value.type == BlobType.Json).then(
      (control: any) => {

        /*
         * we have the control file, now we have to break out the corridors and compute the model
         * for each which includes the details of the cells and how to render on screen etc.
         *
         */

        this._numCorridors = Object.keys(control).length;

        cbc._project.services.taskMonitor.progress(new TaskCreateBatch(cbc._taskId,  `Found ${this._numCorridors}, importing ...`, 10.0));

        Logger.debug(`[CorridorBatchCreator] create - corridors scanned (found ${this._numCorridors} batches)`);

        /* checkpoint: building batch of batches... */

        cbc.buildCorridors(value.batch, value.src, control, value.type == BlobType.Json);
      },
      (error) => {

        /* something went horribly, horribly wrong. */

        cbc._project.services.taskMonitor.progress(new TaskCreateBatchFailed(cbc._taskId,  error));

        Logger.error('[CorridorBatchCreator] create - could not do first time calculation model: ', error);
      });
  }

  /** helper to get cartesian product of two arrays */
  /*
  cartesian(p, t) {

    // ref: https://stackoverflow.com/questions/12303989/cartesian-product-of-multiple-arrays-in-javascript

    return [p, t].reduce((a, b) => a.flatMap(x => b.map(y => [...x, y])), [[]]);
  }
  */

  /** using the "control file" for an import folder, build out the corridor models */
  buildCorridors(batchName: string, importFolder: string, control: any, isPlan: boolean) {

    let cbc: CorridorBatchCreator = this;

    /* checkpoint: building batch import requests */

    Logger.debug(`[CorridorBatchCreator] buildCorridors - creating new batch: `, batchName);

    /*
     * build out the batch group and corridor batch models; these are mostly temporary; to get
     * us to the point where we can import via SWE into an actual batch definition in the
     * user's project.  Once we get a batch definition, we only need to keep details of how
     * to fine the batch we created in SWE and how to render it (i.e. layout details).
     *
     */

    let batchGroup: CorridorGroupBatch = new CorridorGroupBatch(batchName, importFolder);

    let batches: any[]                 = [];
    let errors: any[]                  = [];
    let observables: Observable<any>[] = [];

    let tracker: Map<Observable<any>, CorridorBatch>
                                       = new Map<Observable<any>, CorridorBatch>();

    /*
     * we can crate a node in the project tree for the batch group; even if some imports fail
     * we can either hang an error child off the group, or just not include that corridor in
     * the children.  At this point though, It's ok to create batch group element.
     *
     */

    /*
     * NOTE: for the batch root path location; we locate it:
     *
     *   <project folder> /corrodor_batches/ <unique key> / <corridor name>
     *
     * Notice we are not using the batch name for folder of the corridor batches.  We
     * allow the user in the GUI to rename the batch group (the batch of batches).  But
     * renaming that has no impact on the batches, except for the paths.  So, we
     * intentionally do not use the actual name of the batch as part of the path,
     * instead we use a UUID or "batch key", hence 'bk' below.  This way the batch group
     * name is only a label in the GUI.
     *
     * Eventually if we want to allow corridor batches to be renamed, we can do the same
     * with that final part of the path as well.  The main downside is that it's harder to
     * navigate in the Browse panel, because the folder names won't be obvious anymore.
     *
     */

    const bk: string          = uuidv4();
    const batchFolder: string = cbc._project.dirname() + '/corridor_batches/' + bk;
    let     processed: number = 0;

    let simGroup: SimulationElement = cbc._project.simulation();

    Logger.debug(`[CorridorBatchCreator] adding batch group (${batchName}).`);

    let bge: BatchGroupElement = ProjectElementFactory.fromObject(
      ProjectElementType.BatchGroup,
      {
        project: cbc._project,
             id: '',
           name: batchName,
            key: bk,
         prefix: batchFolder,
         parent: simGroup,
        publish: true
      }) as BatchGroupElement;

    cbc._project.expand([bge]);

    /* save so, we have a check point */

    bge.save();

    /* create the observables that will create the batches (one per corridor)... */

    let corridorLUT: Map<string, CorridorBatch> = new Map<string, CorridorBatch>();

    Object.keys(control).forEach((corridorName) => {

      /*
       * build out this corridor...
       *
       * NOTE: we assume x-axis is traffic and y-axis is the plants
       *
       * We control data to look like:
       *
       *   {
       *     "x_axis": ...
       *     "x_axis": ...
       *     "cells": ...
       *   }
       *
       * x_axis and y_axis provide details about layout order/rendering and the plant or traffic involved.
       * while the cells give the cartesian product and indicate what the combinations are along with which
       * ones are enabled (or not).  Here enabled means valid for use, the user can still later disable
       * a valid for use cell if they want to.
       *
       * the items in the x_axis are typically the traffic (the columns), and the items in the y_axis are
       * typically the plant (the rows).  The SWE doesn't care about layout in the GUI, but we need to
       * ensure we know which part of each combination is plant or traffic.
       *
       * Each axis items look like:
       *
       *   {
       *     "name": "Traffic 1",
       *     "tag": 402489273,
       *     "sort_index": 1,
       *     "blob": "./Corridor 0/Traffic 1.tra",
       *     "pass_to_cell": {
       *       "axis_type": "traffic",
       *       "start_date": "20210201T000000",
       *       "end_date": "20210301T000000"
       *     }
       *   }
       *
       * The blob is the item to use in the cell combo, sort_index tells us which colum or row, and
       * pass_to_cell is detail to merge into the combination (the cell).
       *
       * The cell items look like:
       *
       *   {
       *     "enabled": false,
       *     "tags": [ 402489273, 1101077115 ]
       *   }
       *
       * In the current implementation of control file generation, all cell combinations are *potentially*
       * valid.  Some will be disabled due to time range scoping, but even those could *potentially* be
       * enabled by users.  So, we assume all NxM combinations are valid (we can't do any kind of sparse
       * representation), and we take enabled as provided.
       *
       * Basically our AGGrid rendering will have to show and handle all NxM cells, and the batch in the
       * SWE, will have to have all NxM cells (with some disabled by default).
       *
       */

      const corridor: any       = control[corridorName];

      Logger.debug(`[CorridorBatchCreator] buildCorridors - walking cells in "${corridorName}": `, corridor.cells);

      /* make the columns ... */

      const columnLUT           = new Map<string, object>(corridor.x_axis.map(x => [x.tag, x]));

      /* make the rows ... */

      const rowLUT              = new Map<string, object>(corridor.y_axis.map(y => [y.tag, y]));

      /* get the common settings to use across all the cells ... */

      /*
       * NOTE: we clone over to a temporary working folder just so we can get the right
       * structure expected by the SWE.  The SWE will then internally re-create everything
       * in its own structure, whatever that is.
       *
       */

      const ck: string          = uuidv4();
      const batchRoot: string   = batchFolder + '/' + ck;
      const dst                 = batchRoot + '/' + corridorName;

      let   src: string         = importFolder.replace(/[ ./]+$/, '');

      if (isPlan) {

        /* if they picked a plan file, we assume it's in the top level of the batch folder */

        src = dirname(src).replace(/[ ./]+$/, '');

        Logger.debug(`[CorridorBatchCreator] buildCorridors - src == dir of plan file:`, src);
      }

      let corridorBatch:
        CorridorBatch = new CorridorBatch(corridorName, dst);

      corridorBatch.prefix      = dst;
      corridorBatch.key         = ck;

      /* calculate the cells for this corridor/batch ... */

      corridor.cells.forEach((item: {enabled: boolean, tags: [string, string]}) => {

        /* figure out where the import blobs are, and where we are going to clone them to... */

        let   x: any            = columnLUT.get(item.tags[0]);
        let   y: any            = rowLUT.get(item.tags[1]);

        if (!x || !y) {

          /* if we didn't get a hit, maybe they are reversed? */

          x = columnLUT.get(item.tags[1]);
          y = rowLUT.get(item.tags[0]);
        }

        if (!x || !y) {

          /* bad tags? */

          Logger.warning(`[CorridorBatchCreator] buildCorridors - tag pair is not usable (will ignore cell): `, item);

          return ;
        }

        const cellName: string  = 'cell_' + x.tag + '_' + y.tag;

        const tName: string     = basename(x.blob);
        const tDst: string      = src + '/' + cellName + '/' + tName;
        let   tSrc: string;

        const pName: string     = basename(y.blob);
        const pDst: string      = src + '/' + cellName + '/' + pName;
        let   pSrc: string;

        /*
         * where we import from is the blob for plant (or traffic), but it may be
         * given in absolute or relative format, so we just try to be ready for that...
         *
         */

        if (x.blob.charAt(0) == '.') {

          tSrc = src + '/' + x.blob.replace(/^[ ./]+/, '');

        } else {

          tSrc = x.blob;
        }

        if (y.blob.charAt(0) == '.') {

          pSrc = src + '/' + y.blob.replace(/^[ ./]+/, '');

        } else {

          pSrc = y.blob;
        }

        /* build the cell model */

        let cell: BatchCell = new BatchCell({
          name:        cellName,
          enabled:     item.enabled,
          trafficSrc:  tSrc,
          trafficDst:  tDst,
          plantSrc:    pSrc,
          plantDst:    pDst,
          meta:        {
            ts: x.pass_to_cell.start_date,
            te: x.pass_to_cell.end_date,
            tn: x.name,
            tx: x.sort_index,
            ps: y.pass_to_cell.start_date,
            pe: y.pass_to_cell.end_date,
            pn: y.name,
            py: y.sort_index,
            ct: item.tags
          }
        });

        /* save the cell */

        corridorBatch.cells.set(cellName, cell);

      }); /* end of cell walk */

      /* remember the corridor batch model for this corridor */

      batchGroup.corridors.set(corridorName, corridorBatch);

      corridorLUT.set(batchName + '_' + corridorName, corridorBatch);

      /* create the batch import request for this batch/corridor */

      /* checkpoint: import request created */

      /*
       * for progress; scanning was 10%, and we count creating the batch import requests as 10%, so
       * whatever number of batches we have, it needs to add up to 10%.
       *
       */

      processed++;

      const progress: number = 10.0 + (processed / this._numCorridors) * 10.0;

      cbc._project.services.taskMonitor.progress(new TaskCreateBatch(cbc._taskId,  `Building import request: ${corridorName}`, progress));

      let o: Observable<any> =
         cbc._project.services.swe.importCorridorBatch(
           batchName,
           corridorName,
           batchRoot,
           Array.from(corridorBatch.cells.values()),
           {
             gui_layout: {
               x_axis: corridor.x_axis,
               y_axis: corridor.y_axis
             }
           })

      observables.push(o);

      /* track the observable to corridor, so we can back map later */

      tracker.set(o, corridorBatch);

    }); /* end of corridor walk */

    /*
     * progress should now be at 20%, the next 70% is waiting for the batches to be
     * created / imported.  The final 10% is adding the children elements to the
     * project tree.
     *
     */

    /*
     * now run the observables, but sequentially, so we don't overwhelm the system
     *
     * NOTE: we run the import requests within an async function so that we can
     * await each one, and force them to happen in sequence.  We're doing this
     * instead of forkJoin (parallel) because with multiple corridors where
     * each one might have hundreds of cells...the chances of overwhelming the
     * backend start to go up. :( There is only one blobmanager, eventually all
     * the blob cloning funnels through there.
     *
     * We try to go a little easy, and do them in sequence.  If we eventually
     * decide we want the speed and the backend is strong enough for it...we can
     * just switch to forkJoin.
     *
     * ref: https://javascript.info/async-await
     * ref: https://stackoverflow.com/questions/24586110/resolve-promises-one-after-another-i-e-in-sequence
     * ref: https://dev.to/j3nnning/resolve-promises-in-sequence-with-rxjs-concatmap-58a
     *
     */

    /* checkpoint: importing batches */

    (async (): Promise <any> => {

      /* step through the promises one by one */

      let imported: number = 0;

      for (const obs of observables) {

        let cb: CorridorBatch = tracker.get(obs);

        try {

          /* what corridor was this for? */

          Logger.debug(`[CorridorBatchCreator] buildCorridors - waiting on import:`, cb.name);

          const batchDef: any = await obs.toPromise();

          /* checkpoint: batch created! */

          imported++;

          const progress: number = 20.0 + (imported / this._numCorridors) * 70.0;

          cbc._project.services.taskMonitor.progress(new TaskCreateBatch(cbc._taskId,  `Imported: ${cb.name}`, progress));

          Logger.debug(`[CorridorBatchCreator] buildCorridors - batch def (${cb.name} => ${batchDef.id}):`, batchDef);

          batches.push(batchDef);

          /* map the GUI batch to the SWE batch; once we have this, we can create a valid Corridor child node */

          cb.batchId = batchDef.id;

        } catch (error) {

          /* checkpoint: batch import failed */

          cb.error = error;

          Logger.error(`[CorridorBatchCreator] buildCorridors - batch creation error (${cb.name}): `, error);

          errors.push(error);
        }

      }

      /* at this point, the batches should all be imported */

      if (errors.length > 0) {

        /* checkpoint: errored out, some corridors failed */

        Logger.error(`[CorridorBatchCreator] buildCorridors - some of the corridors failed to import: `, errors);

        if (errors.length == 1) {

          cbc._project.services.taskMonitor.progress(new TaskCreateBatchFailed(cbc._taskId, errors[0]));

        } else {

          cbc._project.services.taskMonitor.progress(new TaskCreateBatchFailed(cbc._taskId, `some of the corridors failed to import`));
        }

        return ;
      }

      /* all the corridors were converted to batches, now back map to our models in the GUI for tracking */

      Logger.debug(`[CorridorBatchCreator] buildCorridors - batches: `, batches);

      /* for each of the error corridors, create the element, but in error state */

      let created: number = 0;

      batches.forEach((batchDef: any) => {

        /* map GUI cell to SWE cell */

        let corridor: CorridorBatch = corridorLUT.get(batchDef.name);

        corridor.batchId = batchDef.id;

        /* create a child corridor element under the batch group */

        Logger.debug(`[CorridorBatchCreator] adding corridor batch node (${batchName} >> ${corridor.name}).`, corridor);

        let cbe: CorridorBatchElement = ProjectElementFactory.fromObject(
          ProjectElementType.CorridorBatch,
          {
            project: cbc._project,
                 id: '',
               name: corridor.name,
                key: corridor.key,
             prefix: corridor.prefix,
              batch: corridor.batchId,
              error: corridor.error,
             parent: bge,
            publish: true
          }) as CorridorBatchElement;

        cbc._project.expand([bge._parent, bge, cbe]);

        created++;

        const progress: number = 90.0 + (created / this._numCorridors) * 10.0;

        cbc._project.services.taskMonitor.progress(new TaskCreateBatch(cbc._taskId,  `Batch added to project: ${batchName}`, progress));

      }); /* end of imported corridor batch walk */

      /* save so, we have a check point */

      bge.save();

      cbc._project.services.taskMonitor.progress(new TaskCreateBatchComplete(cbc._taskId, `Batch of batches created: ${batchName}`));

    })();

  }

  /** validate the parameters we got from the dialog */
  validate(value: CorridorBatchDialogParams): string {

    let cbc: CorridorBatchCreator = this;
    let parent: ProjectElement    = cbc._project.locomotiveModels();
    let batch: string             = value.batch;
    let src: string               = value.src;

    Logger.debug(`[CorridorBatchCreator] validating: batch=${batch} src=${src}`);

    /* is it a valid name? */

    if (batch.trim() === '') {

      return 'Corridor can not be empty.';
    }

    /* do we already have a model of this name? */

    const peers: ProjectElement[] = parent._children;

    for (const peer of peers) {

      if (peer._label === batch) {

        return 'Batch of that name already exists in project.';
      }

    }

    if (value.src === '') {

      return 'Must choose folder to import from';
    }

    /* its golden! */

    value.batch = batch;

    return '';
  }

}
